import { apiGet } from './client';
import type { OperationResult } from './types';

// ── DTO mirrors of OO.Business.AIInfo.Interfaces.IInsightsManager ─────────

export interface TokenCostBucket {
  agentId?: number | null;
  agentName?: string | null;
  modelName?: string | null;
  callCount: number;
  inputTokens: number;
  outputTokens: number;
  cachedTokens: number;
  /** Sum of STORED costs — a floor whenever callsWithoutRate or callsStoredAsZero is above zero. */
  estimatedCostUsd: number;
  /** Calls with no cost stored: no rate matched the model when they ran. */
  callsWithoutRate: number;
  /** Calls that used tokens but stored $0.00 (rows before 2026-09-07 were rounded to whole cents). */
  callsStoredAsZero: number;
  avgDurationMs: number;
  /** ISO timestamp of the most recent call in this bucket. Null when the bucket has no calls. */
  lastSeenUtc?: string | null;
  /** Whether ai.AIModel.Active = 1 for the matching model row. Null = unknown (no matching model row). */
  modelIsActive?: boolean | null;
}

export interface ContextOverloadBucket {
  agentId?: number | null;
  agentName?: string | null;
  modelName?: string | null;
  criticalCount: number;
  warningCount: number;
  healthyCount: number;
  callCount: number;
  avgPercentOfContext?: number | null;
  maxPercentOfContext?: number | null;
  lastSeenUtc?: string | null;
  modelIsActive?: boolean | null;
}

export interface CompactionBucket {
  strategyUsed: string;
  appliedCount: number;
  totalTokensBefore: number;
  totalTokensAfter: number;
  totalMessagesRemoved: number;
  avgTokenReductionPct: number;
}

export interface RouterAccuracyBucket {
  day: string; // ISO date
  routedCount: number;
  failedFallbackCount: number;
  noSkillsLinkedCount: number;
  disabledCount: number;
  avgElapsedMs: number;
}

export interface SafetyFlakinessBucket {
  scenarioId: string;
  agentId?: number | null;
  agentName?: string | null;
  runCount: number;
  passCount: number;
  failCount: number;
  passRatePct: number;
  avgScore: number;
}

export interface ToolOverflowBucket {
  pluginName: string;
  methodName: string;
  callCount: number;
  medianArgChars: number;
  maxArgChars: number;
  medianResponseChars: number;
  maxResponseChars: number;
  modelMaxOutputTokens?: number | null;
  errorCount: number;
}

export interface A2ALatencyBucket {
  primaryAgentId?: number | null;
  primaryAgentName?: string | null;
  peerAgentId?: number | null;
  peerAgentName?: string | null;
  callCount: number;
  medianMs: number;
  p95Ms: number;
  maxMs: number;
  timeoutCount: number;
  errorCount: number;
}

// ── Tier 2 (added 2026-05-01) — five operator tiles built off existing
//    log tables. Mirror the C# DTOs in IInsightsManager.cs.

export interface RunLatencyBucket {
  agentId?: number | null;
  agentName?: string | null;
  modelName?: string | null;
  callCount: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
}

export interface ErrorMixBucket {
  agentId?: number | null;
  agentName?: string | null;
  /** "rate_limit_429" | "server_error_5xx" | "content_filter_503" | "client_error_4xx" | "tool_outputs_invalid" | … */
  errorKind: string;
  count: number;
  lastSeenUtc: string;
}

export interface ToolPerformanceBucket {
  pluginName: string;
  methodName: string;
  callCount: number;
  p50Ms: number;
  p95Ms: number;
  p99Ms: number;
  maxMs: number;
  errorCount: number;
  errorRatePct: number;
}

export interface ThreadContextBucket {
  threadId: string;
  agentId?: number | null;
  agentName?: string | null;
  callCount: number;
  avgPercentOfContext?: number | null;
  maxPercentOfContext?: number | null;
  firstSeenUtc: string;
  lastSeenUtc: string;
}

export interface ConversationCostBucket {
  threadId: string;
  agentId?: number | null;
  agentName?: string | null;
  callCount: number;
  totalTokens: number;
  /** Sum of STORED costs — a floor when callsMissingCost is above zero. */
  estimatedCostUsd: number;
  callsMissingCost: number;
  firstSeenUtc: string;
  lastSeenUtc: string;
}

export interface ConversationCostStats {
  conversationCount: number;
  /** Conversations holding a call with no usable cost — their cost, and the total, are floors. */
  conversationsMissingCost: number;
  totalCostUsd: number;
  medianCostUsd: number;
  p95CostUsd: number;
  p99CostUsd: number;
  maxCostUsd: number;
}

export interface ConversationCostReport {
  top: ConversationCostBucket[];
  stats: ConversationCostStats;
}

/**
 * Spend across token-cost buckets, and how many calls it does NOT cover. The Insights tile and the
 * Dashboard tile both read this, so neither can quietly present a floor as a total again.
 */
export function spendCoverage(buckets: TokenCostBucket[]): { usd: number; calls: number; missing: number } {
  return buckets.reduce(
    (acc, b) => ({
      usd: acc.usd + Number(b.estimatedCostUsd ?? 0),
      calls: acc.calls + b.callCount,
      missing: acc.missing + (b.callsWithoutRate ?? 0) + (b.callsStoredAsZero ?? 0),
    }),
    { usd: 0, calls: 0, missing: 0 }
  );
}

// ── API calls ─────────────────────────────────────────────────────────────

async function get<T>(url: string, params: Record<string, unknown> = {}): Promise<T> {
  const wrapped = await apiGet<OperationResult<T>>(url, params);
  if (!wrapped.isSuccess || wrapped.data == null) {
    throw new Error(wrapped.message || 'Insights query failed');
  }
  return wrapped.data;
}

export const fetchTokenCost = (days = 30) =>
  get<TokenCostBucket[]>('/api/Insights/TokenCost', { days });

export const fetchContextOverload = (days = 30) =>
  get<ContextOverloadBucket[]>('/api/Insights/ContextOverload', { days });

export const fetchCompaction = (days = 30) =>
  get<CompactionBucket[]>('/api/Insights/Compaction', { days });

export const fetchRouterAccuracy = (days = 30) =>
  get<RouterAccuracyBucket[]>('/api/Insights/RouterAccuracy', { days });

export const fetchSafetyFlakiness = (minRuns = 2) =>
  get<SafetyFlakinessBucket[]>('/api/Insights/SafetyFlakiness', { minRuns });

/**
 * Tool overflow ranking. When `agentId` is provided (>0), the result is
 * scoped to one agent — drives the dashboard "Top pressures" → tile
 * drill-in. Omit `agentId` for the legacy merchant-wide ranking.
 */
export const fetchToolOverflowRisk = (days = 30, agentId?: number | null) =>
  get<ToolOverflowBucket[]>(
    '/api/Insights/ToolOverflowRisk',
    agentId && agentId > 0 ? { days, agentId } : { days },
  );

export const fetchA2ALatency = (days = 30) =>
  get<A2ALatencyBucket[]>('/api/Insights/A2ALatency', { days });

// Tier 2 fetchers — match the routes on InsightsController.

export const fetchRunLatency = (days = 30) =>
  get<RunLatencyBucket[]>('/api/Insights/RunLatency', { days });

export const fetchErrorMix = (days = 30) =>
  get<ErrorMixBucket[]>('/api/Insights/ErrorMix', { days });

export const fetchToolPerformance = (days = 30) =>
  get<ToolPerformanceBucket[]>('/api/Insights/ToolPerformance', { days });

export const fetchThreadContext = (days = 30, top = 25) =>
  get<ThreadContextBucket[]>('/api/Insights/ThreadContext', { days, top });

export const fetchConversationCost = (days = 30, top = 25) =>
  get<ConversationCostReport>('/api/Insights/ConversationCost', { days, top });
